import { Injectable } from "@angular/core";
import { AbstractControl, ValidationErrors, ValidatorFn } from "@angular/forms";

export interface PasswordApiError {
  translationKey?: string;
  rawMessage?: string;
}

@Injectable({ providedIn: "root" })
export class PasswordService {
  validator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value || "";
      const errors: any = {};
      if (value.length < 16) errors.minLength = true;
      if (!/[A-Z]/.test(value)) errors.uppercase = true;
      if (!/[0-9]/.test(value)) errors.number = true;
      if (!/[@.\-*&#]/.test(value)) errors.specialChar = true;
      return Object.keys(errors).length > 0 ? errors : null;
    };
  }

  checkStrength(value: string) {
    return {
      minLength: value.length >= 16,
      uppercase: /[A-Z]/.test(value),
      number: /[0-9]/.test(value),
      specialChar: /[@.\-*&#]/.test(value),
    };
  }

  /**
   * Maps a Gigya API error response to a displayable error.
   * @param codeOverrides Override the default translation key for specific error codes.
   *   Useful when the same code means something different depending on the API called
   *   (e.g. 400006 = "same as current" in setAccountInfo vs "too weak" in resetPassword).
   */
  parseError(
    res: any,
    codeOverrides?: Map<number, string>
  ): PasswordApiError | null {
    if (res.errorCode === 0) return null;

    if (codeOverrides?.has(res.errorCode)) {
      return { translationKey: codeOverrides.get(res.errorCode) };
    }

    switch (res.errorCode) {
      case 401030:
        return { translationKey: "updatePassword.oldPasswordError" };
      case 400006:
        return { translationKey: "updatePassword.weakPasswordError" };
      case 400060:
        return { translationKey: "updatePassword.compromisedPasswordError" };
      case 403036:
        return { translationKey: "updatePassword.expiredTokenError" };
      case 206001:
        return { translationKey: "updatePassword.pendingRegistrationError" };
      case 206002:
        return { translationKey: "updatePassword.pendingVerificationError" };
      case 403013:
        return { translationKey: "updatePassword.invalidAccountError" };
      default:
        return {
          rawMessage:
            res.errorMessage ||
            "An unexpected error occurred. Please try again.",
        };
    }
  }
}
